import { jsPDF } from 'jspdf';
import { addLogoToPdfHeader } from './pdfLogoHelper';
import * as BABYLON from '@babylonjs/core';
import { calculateTrussLoad } from './TrussSystem';
import { TRUSS_LIBRARY, CORNER_BLOCKS, BASE_PLATES, calculateSections } from './TrussData';

const VIEWS = [
  { name: 'Front View', alpha: Math.PI / 2, beta: Math.PI / 2 },
  { name: 'Back View', alpha: -Math.PI / 2, beta: Math.PI / 2 },
  { name: 'Left View', alpha: Math.PI, beta: Math.PI / 2 },
  { name: 'Right View', alpha: 0, beta: Math.PI / 2 },
  { name: 'Top View', alpha: 0, beta: 0.01 },
  { name: 'Isometric View', alpha: Math.PI / 4, beta: Math.PI / 3 },
];

function captureView(scene, engine, target, distance, view) {
  return new Promise((resolve) => {
    const cam = new BABYLON.ArcRotateCamera('trussReportCam', view.alpha, view.beta, distance, target, scene);
    cam.minZ = 0.1;
    cam.maxZ = 500;
    const prevCam = scene.activeCamera;
    scene.activeCamera = cam;
    let frames = 0;
    const obs = scene.onAfterRenderObservable.add(() => {
      frames++;
      if (frames >= 3) {
        scene.onAfterRenderObservable.remove(obs);
        BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, cam, { width: 1200, height: 900 }, (data) => {
          scene.activeCamera = prevCam;
          cam.dispose();
          resolve(data);
        });
      }
    });
  });
}

/**
 * Get a human-readable label for a span key.
 */
function spanLabel(key) {
  const labels = {
    span: 'Straight Span',
    leftLeg: 'Left Leg',
    rightLeg: 'Right Leg',
    topSpan: 'Top Span',
    pillar: 'Pillar',
    widthSpan: 'Width Span',
    depthSpan: 'Depth Span',
    arm1: 'Arm 1',
    arm2: 'Arm 2',
  };
  if (key.startsWith('grid_')) return `Grid Truss #${parseInt(key.split('_')[1]) + 1}`;
  return labels[key] || key;
}

/**
 * Get structure-specific dimension text.
 */
function getDimensionText(tc) {
  const st = tc.structureType || 'straight';
  switch (st) {
    case 'straight':
      return `Length: ${tc.length || 3} m`;
    case 'goalpost': {
      const gp = tc.goalpost || {};
      return `Left: ${gp.heightLeft || 5}m | Right: ${gp.heightRight || 5}m | Top: ${gp.topWidth || 6}m`;
    }
    case 'box_system': {
      const bs = tc.boxSystem || {};
      return `W: ${bs.width || 8}m | D: ${bs.depth || 6}m | H: ${bs.height || 6}m`;
    }
    case 'circular': {
      const c = tc.circular || {};
      return `Radius: ${c.radius || 3}m | Arc: ${c.arcAngle || 360}\u00B0 | Segments: ${c.segments || 12}`;
    }
    case 'angle': {
      const a = tc.angle || {};
      return `Arm1: ${a.length1 || 2}m | Arm2: ${a.length2 || 2}m | Angle: ${a.angle || 90}\u00B0`;
    }
    default:
      return `Length: ${tc.length || 3} m`;
  }
}

export async function exportTrussReport(scene, engine, trussConfig, projectName, userName) {
  const tc = trussConfig;
  const ld = calculateTrussLoad(tc);
  const profileId = tc.trussProfile || 'F34';
  const libProfile = TRUSS_LIBRARY[profileId] || TRUSS_LIBRARY['F34'];

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pw = 210;
  const ph = 297;
  const m = 12;
  const cw = pw - m * 2;
  let y = m;

  // ===== Helper functions =====
  const hdr = (text, size) => {
    pdf.setFontSize(size || 11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(text, m, y);
    y += 5;
    pdf.setDrawColor(140, 140, 140);
    pdf.line(m, y, pw - m, y);
    y += 4;
  };

  const row = (label, value, color) => {
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(90, 90, 90);
    pdf.text(label, m + 2, y);
    if (color) pdf.setTextColor(...color);
    else pdf.setTextColor(30, 30, 30);
    pdf.setFont('helvetica', 'bold');
    pdf.text(String(value), m + 85, y);
    y += 4.5;
  };

  const sep = (s) => { y += (s || 2); };

  const checkPage = (needed) => {
    if (y + needed > ph - m) {
      pdf.addPage();
      y = m;
    }
  };

  // ===== PAGE 1: Header + Overview =====
  // Dark header banner
  pdf.setFillColor(18, 24, 38);
  pdf.rect(0, 0, pw, 36, 'F');
  // Cyan accent line
  pdf.setFillColor(0, 200, 220);
  pdf.rect(0, 36, pw, 1.5, 'F');
  // AV Simulation Tool logo (top-right of header bar)
  await addLogoToPdfHeader(pdf, { x: pw - m - 26, y: 4, height: 16 });
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Truss Rigging Report', m, 18);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(180, 200, 220);
  pdf.text('Generated by AV Simulation Tool', m, 27);
  pdf.setFontSize(7);
  pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }), pw - m - 55, 27);

  y = 44;

  // Project info row
  pdf.setFontSize(8);
  pdf.setTextColor(90, 90, 90);
  pdf.text('Project:', m, y);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 30, 30);
  pdf.text(projectName || 'Untitled', m + 18, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(90, 90, 90);
  pdf.text('Prepared by:', m + 80, y);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 30, 30);
  pdf.text(userName || 'System User', m + 105, y);
  y += 7;

  // Safety disclaimer
  pdf.setFillColor(255, 245, 210);
  pdf.roundedRect(m, y, cw, 10, 1.5, 1.5, 'F');
  pdf.setFontSize(6.5);
  pdf.setTextColor(140, 100, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ENGINEERING NOTICE:', m + 3, y + 4);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Load data from manufacturer tables (Global Truss). Final rigging must be verified by a certified structural engineer.', m + 38, y + 4);
  pdf.text('All values are theoretical maximums under ideal conditions. Dynamic loads, wind, and environmental factors are not included.', m + 3, y + 8);
  y += 14;

  // ===== Profile & Structure Overview =====
  hdr('Truss Profile');
  const leftX = m + 2;
  const rightX = m + cw / 2 + 5;

  // Left column: profile details
  pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
  pdf.text('Profile:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30); pdf.text(ld.profileLabel, leftX + 25, y);
  y += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
  pdf.text('Geometry:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30); pdf.text(ld.geometry.charAt(0).toUpperCase() + ld.geometry.slice(1), leftX + 25, y);
  y += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
  pdf.text('Material:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30); pdf.text(ld.material, leftX + 25, y);
  y += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
  pdf.text('Cross Section:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30); pdf.text(`${ld.size_mm} mm`, leftX + 25, y);
  y += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
  pdf.text('Weight/m:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30); pdf.text(`${ld.weightPerM} kg/m`, leftX + 25, y);
  y += 6;

  hdr('Structure Dimensions');
  row('Type:', ld.structureType.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()));
  row('Dimensions:', getDimensionText(tc));
  row('Total Length:', `${ld.totalLength} m`);
  row('Critical Span:', `${ld.mainSpan} m`);
  row('Total Weight:', `${ld.totalWeight} kg`);
  sep(2);

  // ===== Load Capacity =====
  checkPage(50);
  hdr('Load Capacity');

  // Load capacity cards (side by side)
  const cardW = (cw - 6) / 3;
  const cardY = y;
  const loadCards = [
    { label: 'Max UDL', value: `${ld.maxUDL} kg`, color: [0, 170, 200] },
    { label: 'Point Load', value: `${ld.maxPointLoad} kg`, color: [0, 170, 200] },
    { label: 'Deflection', value: `${ld.deflection} mm`, color: [220, 160, 0] },
  ];

  loadCards.forEach((card, i) => {
    const cx = m + i * (cardW + 3);
    pdf.setFillColor(240, 245, 250);
    pdf.roundedRect(cx, cardY, cardW, 14, 1.5, 1.5, 'F');
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(card.label, cx + 3, cardY + 5);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...card.color);
    pdf.text(card.value, cx + 3, cardY + 12);
  });
  y = cardY + 18;

  if (ld.loadInterpolated) {
    pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
    pdf.text(`* Load values interpolated for ${ld.mainSpan}m span`, leftX, y);
    y += 4;
  }
  if (ld.exceedsMaxSpan) {
    pdf.setFillColor(255, 230, 230);
    pdf.roundedRect(m, y, cw, 7, 1, 1, 'F');
    pdf.setFontSize(7); pdf.setTextColor(200, 0, 0); pdf.setFont('helvetica', 'bold');
    pdf.text('WARNING: Exceeds max rated span for this profile. Consult manufacturer.', m + 3, y + 4.5);
    y += 10;
  }

  // Applied load status
  if ((tc.appliedLoad || 0) > 0) {
    sep(1);
    const statusColor = ld.loadStatusColor === 'green' ? [0, 140, 0] : ld.loadStatusColor === 'yellow' ? [200, 170, 0] : [200, 0, 0];
    row('Applied Load:', `${ld.appliedLoad} kg`);
    row('Load Status:', `${ld.loadStatusText} (${Math.round(ld.loadRatio * 100)}% capacity)`, statusColor);
  }

  // Span validation
  if (!ld.spanValid && ld.spanMessage) {
    sep(2);
    pdf.setFontSize(7); pdf.setTextColor(200, 100, 0);
    pdf.text(`Span Note: ${ld.spanMessage}`, leftX, y);
    y += 4;
  }

  // ===== PAGE 2: Section Breakdown + Hardware BOM =====
  pdf.addPage();
  y = m;

  hdr('Section Breakdown', 12);

  const breakdowns = ld.sectionBreakdowns || {};
  const spanKeys = Object.keys(breakdowns);

  if (spanKeys.length > 0) {
    // Table header
    pdf.setFillColor(235, 240, 245);
    pdf.rect(m, y, cw, 6, 'F');
    pdf.setFontSize(7); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(50, 50, 50);
    pdf.text('Span', m + 2, y + 4);
    pdf.text('Profile', m + 40, y + 4);
    pdf.text('Length', m + 80, y + 4);
    pdf.text('Sections', m + 105, y + 4);
    pdf.text('Joints', m + 140, y + 4);
    pdf.text('Breakdown', m + 160, y + 4);
    y += 8;

    for (const key of spanKeys) {
      checkPage(8);
      const bd = breakdowns[key];
      const profLabel = (TRUSS_LIBRARY[bd.profileId] || {}).label || bd.profileId;

      // Alternating row background
      if (spanKeys.indexOf(key) % 2 === 0) {
        pdf.setFillColor(248, 250, 252);
        pdf.rect(m, y - 3, cw, 5.5, 'F');
      }

      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(60, 60, 60);
      pdf.text(spanLabel(key), m + 2, y);
      pdf.setFontSize(6.5); pdf.setTextColor(100, 100, 100);
      pdf.text(profLabel.substring(0, 20), m + 40, y);
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30);
      pdf.setFontSize(7.5);
      pdf.text(`${bd.length} m`, m + 80, y);
      pdf.text(`${bd.sections.length}`, m + 110, y);
      pdf.text(`${bd.jointCount}`, m + 145, y);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(6.5);
      const breakdownStr = bd.sections.map(s => `${s}m`).join(' + ');
      pdf.text(breakdownStr.substring(0, 25), m + 160, y);
      y += 5.5;

      // Note for box systems: indicate multiplier
      if (key === 'pillar') {
        const bs = tc.boxSystem || {};
        let pc = bs.pillarCount || 4;
        if ((bs.pillarMode || 'auto') === 'auto') {
          if ((bs.width || 8) > 20) pc = 8;
          else if ((bs.width || 8) > 10) pc = 6;
          else pc = 4;
        }
        if (pc > 1) {
          pdf.setFontSize(6); pdf.setTextColor(100, 100, 140);
          pdf.text(`  (x${pc} pillars)`, m + 4, y);
          y += 4;
        }
      }
      if (key === 'widthSpan' || key === 'depthSpan') {
        pdf.setFontSize(6); pdf.setTextColor(100, 100, 140);
        pdf.text(`  (x2 ${key === 'widthSpan' ? 'front + back' : 'left + right'})`, m + 4, y);
        y += 4;
      }
    }

    // Totals line
    sep(2);
    pdf.setDrawColor(160, 160, 160);
    pdf.line(m, y, pw - m, y);
    y += 4;
    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
    pdf.text(`Total: ${ld.totalSections} sections | ${ld.totalJoints} joints | ${ld.cornerBlockCount} corner blocks`, m + 2, y);
    y += 8;
  }

  // ===== Section Inventory =====
  const sectionCounts = ld.sectionCounts || {};
  const sectionKeys = Object.keys(sectionCounts);

  if (sectionKeys.length > 0) {
    checkPage(30);
    hdr('Section Inventory (Piece Count)');

    // Cards layout
    const maxPerRow = 4;
    const invCardW = (cw - (maxPerRow - 1) * 3) / maxPerRow;
    const sortedSections = sectionKeys.sort((a, b) => parseFloat(b) - parseFloat(a));

    for (let i = 0; i < sortedSections.length; i++) {
      const col = i % maxPerRow;
      const rowIdx = Math.floor(i / maxPerRow);
      if (col === 0 && rowIdx > 0) { y += 16; checkPage(16); }

      const cx = m + col * (invCardW + 3);
      const cy = y;

      pdf.setFillColor(240, 248, 255);
      pdf.setDrawColor(200, 220, 240);
      pdf.roundedRect(cx, cy, invCardW, 13, 1.5, 1.5, 'FD');
      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0, 120, 180);
      pdf.text(`${sectionCounts[sortedSections[i]]}x`, cx + 3, cy + 6);
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(60, 60, 60);
      pdf.text(sortedSections[i], cx + invCardW - 10, cy + 6);
      pdf.setFontSize(6); pdf.setTextColor(130, 130, 130);
      pdf.text('section', cx + invCardW - 14, cy + 10);
    }
    y += 18;
  }

  // ===== Hardware BOM (Bill of Materials) =====
  if (ld.totalJoints > 0) {
    checkPage(45);
    hdr('Hardware Bill of Materials (BOM)');

    const bomItems = [
      { label: 'Truss Sections', qty: ld.totalSections, unit: 'pcs', note: 'See inventory above' },
      { label: 'Corner Blocks', qty: ld.cornerBlockCount, unit: 'pcs', note: getCornerBlockNote(tc) },
      { label: 'Coupler Spigots', qty: ld.totalJoints * 4, unit: 'pcs', note: '1 per chord per joint' },
      { label: 'Conical Pins', qty: ld.totalCouplerPins, unit: 'pcs', note: '2 per chord per joint' },
      { label: 'R-Clips', qty: ld.totalClips, unit: 'pcs', note: '1 per pin' },
    ];

    // Add base plates if enabled
    if (tc.showBasePlates !== false) {
      const bpCount = getBasePlateCount(tc);
      const bpModel = tc.basePlateModel ? (BASE_PLATES[tc.basePlateModel] || {}).label || tc.basePlateModel : 'Standard';
      bomItems.push({ label: 'Base Plates', qty: bpCount, unit: 'pcs', note: bpModel });
    }

    // Table header
    pdf.setFillColor(250, 245, 235);
    pdf.rect(m, y, cw, 6, 'F');
    pdf.setFontSize(7); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(50, 50, 50);
    pdf.text('Item', m + 2, y + 4);
    pdf.text('Qty', m + 80, y + 4);
    pdf.text('Unit', m + 105, y + 4);
    pdf.text('Notes', m + 125, y + 4);
    y += 8;

    for (let i = 0; i < bomItems.length; i++) {
      checkPage(6);
      const item = bomItems[i];
      if (i % 2 === 0) {
        pdf.setFillColor(252, 250, 248);
        pdf.rect(m, y - 3, cw, 5.5, 'F');
      }
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(50, 50, 50);
      pdf.text(item.label, m + 2, y);
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0, 120, 80);
      pdf.text(`${item.qty}`, m + 83, y);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 100, 100);
      pdf.text(item.unit, m + 105, y);
      pdf.setFontSize(6.5); pdf.setTextColor(130, 130, 130);
      pdf.text(item.note || '', m + 125, y);
      y += 5.5;
    }

    // Total weight summary
    sep(3);
    pdf.setDrawColor(180, 160, 140);
    pdf.line(m, y, m + 100, y);
    y += 4;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
    pdf.text(`Estimated Truss Weight: ${ld.totalWeight} kg`, m + 2, y);
    y += 6;
  }

  // ===== Accessory Details =====
  checkPage(30);
  const selectedCornerBlock = tc.cornerBlockModel;
  const selectedBasePlate = tc.basePlateModel;

  if (selectedCornerBlock || selectedBasePlate) {
    hdr('Selected Accessories');
    if (selectedCornerBlock && CORNER_BLOCKS[selectedCornerBlock]) {
      const cb = CORNER_BLOCKS[selectedCornerBlock];
      row('Corner Block:', `${cb.label} — ${cb.description}`);
      row('Type:', `${cb.type}, ${cb.angle}\u00B0`);
      row('Weight (each):', `${cb.weight} kg`);
      sep(2);
    }
    if (selectedBasePlate && BASE_PLATES[selectedBasePlate]) {
      const bp = BASE_PLATES[selectedBasePlate];
      row('Base Plate:', `${bp.label} — ${bp.description}`);
      row('Size:', `${bp.size_mm}mm, ${bp.thickness_mm}mm thick`);
      row('Max Load:', `${bp.maxLoad_kg} kg`);
      row('Weight (each):', `${bp.weight} kg`);
    }
  }

  // ===== VIEW RENDERS: 2 per page =====
  const trussNodes = scene.transformNodes.filter(n => n.metadata?.isTruss);
  let target = BABYLON.Vector3.Zero();
  let camDist = 15;

  if (trussNodes.length > 0) {
    const node = trussNodes[0];
    const children = node.getChildMeshes();
    if (children.length > 0) {
      let mn = children[0].getBoundingInfo().boundingBox.minimumWorld.clone();
      let mx = children[0].getBoundingInfo().boundingBox.maximumWorld.clone();
      for (let i = 1; i < children.length; i++) {
        if (!children[i].isVisible && children[i].visibility === 0) continue;
        const b = children[i].getBoundingInfo();
        mn = BABYLON.Vector3.Minimize(mn, b.boundingBox.minimumWorld);
        mx = BABYLON.Vector3.Maximize(mx, b.boundingBox.maximumWorld);
      }
      target = BABYLON.Vector3.Center(mn, mx);
      camDist = BABYLON.Vector3.Distance(mn, mx) * 1.2;
    }
  }

  for (let vi = 0; vi < VIEWS.length; vi += 2) {
    pdf.addPage();
    for (let j = 0; j < 2 && vi + j < VIEWS.length; j++) {
      const idx = vi + j;
      const topY = m + j * 138;
      try {
        const imgData = await captureView(scene, engine, target, camDist, VIEWS[idx]);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(40, 40, 40);
        pdf.text(VIEWS[idx].name, m, topY + 5);
        pdf.addImage(imgData, 'PNG', m, topY + 8, cw, cw * 0.68);
      } catch (err) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(VIEWS[idx].name, m, topY + 5);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Capture failed', m, topY + 15);
      }
    }
  }

  // ===== Legend & Footer =====
  pdf.addPage();
  y = m;
  hdr('Load Status Legend');
  const legend = [
    { label: 'Safe (< 70%)', color: [0, 140, 0] },
    { label: 'Warning (70-100%)', color: [220, 190, 0] },
    { label: 'Overload (> 100%)', color: [200, 0, 0] },
  ];
  let lx = m + 2;
  for (const item of legend) {
    pdf.setFillColor(...item.color);
    pdf.rect(lx, y - 2, 5, 4, 'F');
    pdf.setFontSize(8); pdf.setTextColor(50, 50, 50); pdf.setFont('helvetica', 'normal');
    pdf.text(item.label, lx + 7, y + 1);
    lx += 50;
  }
  y += 10;

  hdr('Notes');
  const notes = [
    'All load data sourced from Global Truss manufacturer specifications.',
    'UDL = Uniformly Distributed Load. Point Load = Single centered load.',
    'Section breakdowns use standard available truss segment lengths.',
    'Joint hardware counts: Each joint requires 1 spigot per chord, 2 conical pins per chord, and 1 R-clip per pin.',
    'Corner block quantities are based on the structure geometry and do not include any additional bracing connections.',
    'This report does not account for dynamic loads, wind loads, or seismic conditions.',
  ];
  pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
  for (const note of notes) {
    checkPage(6);
    pdf.text(`\u2022  ${note}`, m + 2, y);
    y += 4.5;
  }

  sep(6);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(m, y, pw - m, y);
  y += 6;
  pdf.setFontSize(7); pdf.setTextColor(160, 160, 160);
  pdf.text('AV Simulation Tool \u2014 Truss Rigging Report', m, y);
  pdf.text(`Page count: ${pdf.getNumberOfPages()}`, pw - m - 30, y);

  const fileName = `Truss_Report_${(projectName || 'Project').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(fileName);
  return fileName;
}

// ===== Helper: get corner block note =====
function getCornerBlockNote(tc) {
  if (tc.cornerBlockModel && CORNER_BLOCKS[tc.cornerBlockModel]) {
    return CORNER_BLOCKS[tc.cornerBlockModel].label;
  }
  const st = tc.structureType || 'straight';
  const typeMap = { goalpost: '2-way', box_system: '4-way', angle: '2-way' };
  return typeMap[st] || 'N/A';
}

// ===== Helper: get base plate count =====
function getBasePlateCount(tc) {
  const st = tc.structureType || 'straight';
  switch (st) {
    case 'straight': return 2;
    case 'goalpost': return 2;
    case 'box_system': {
      const bs = tc.boxSystem || {};
      let pc = bs.pillarCount || 4;
      if ((bs.pillarMode || 'auto') === 'auto') {
        if ((bs.width || 8) > 20) pc = 8;
        else if ((bs.width || 8) > 10) pc = 6;
        else pc = 4;
      }
      return pc;
    }
    case 'circular': return 0;
    case 'angle': return 0;
    default: return 2;
  }
}
